import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";

import { api } from "../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { Wordmark } from "@/components/shell/wordmark";
import { AVAILABLE_ROOMS } from "@/lib/rooms";
import { RECIPES_BY_ID, type RoomRecipe } from "@/lib/room-recipes";
import {
  STANDALONE_RECIPE_IDS,
  VIBES,
  resolveRecipeForRoom,
  type Vibe,
} from "@/lib/vibes";
import { WizardShell, type WizardStep } from "@/components/onboarding/wizard-shell";
import { VibeCard } from "@/components/onboarding/vibe-card";
import { RoomTile, StandaloneRecipeTile } from "@/components/onboarding/room-tile";
import {
  PreviewRoomCard,
  type PreviewSubject,
} from "@/components/onboarding/preview-room-card";
import { InviteStep } from "@/components/onboarding/invite-step";
import { cn } from "@/lib/utils";
import { said } from "@/lib/errors";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
});

type Step = "name" | "vibe" | "rooms" | "preview" | "invite";

const STEPS = [
  { key: "name", labelKey: "steps.name" },
  { key: "vibe", labelKey: "steps.vibe" },
  { key: "rooms", labelKey: "steps.rooms" },
  { key: "preview", labelKey: "steps.preview" },
  { key: "invite", labelKey: "steps.invite" },
] as const satisfies readonly { key: Step; labelKey: string }[];

const STANDALONE_RECIPES: RoomRecipe[] = STANDALONE_RECIPE_IDS
  .map((id) => RECIPES_BY_ID[id])
  .filter((r): r is RoomRecipe => Boolean(r));

const MAX_VIBES = 3;

function OnboardingPage() {
  const { t } = useTranslation("onboarding");
  const navigate = useNavigate();
  const create = useMutation(api.workspaces.create);
  const accept = useMutation(api.workspaces.acceptInvite);

  const [mode, setMode] = useState<"create" | "join">("create");
  const [step, setStep] = useState<Step>("name");

  const [name, setName] = useState(() => t("name.defaultName"));
  const [vibeKeys, setVibeKeys] = useState<string[]>(["scandi"]);
  const [selectedRooms, setSelectedRooms] = useState<Set<string>>(
    new Set(AVAILABLE_ROOMS.map((r) => r.type)),
  );
  const [selectedStandalones, setSelectedStandalones] = useState<Set<string>>(
    new Set(),
  );
  const [showStandalones, setShowStandalones] = useState(false);
  // Optional: once the household is made, go on to bring in the home's plans.
  const [fromPlans, setFromPlans] = useState(false);

  const [creating, setCreating] = useState(false);

  // Join branch
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  function toggleVibe(key: string) {
    setVibeKeys((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= MAX_VIBES) return prev;
      return [...prev, key];
    });
  }

  function toggleRoom(type: string) {
    setSelectedRooms((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function toggleStandalone(id: string) {
    setSelectedStandalones((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Every preset room. */
  function selectAllRooms() {
    setSelectedRooms(new Set(AVAILABLE_ROOMS.map((r) => r.type)));
  }

  /** Convert wizard state → workspaces.create args. */
  const seedArgs = useMemo(() => {
    const bareRooms: string[] = [];
    const recipeIds: string[] = [];
    for (const room of AVAILABLE_ROOMS) {
      if (!selectedRooms.has(room.type)) continue;
      const matched = resolveRecipeForRoom(room.type, vibeKeys);
      if (matched) recipeIds.push(matched);
      else bareRooms.push(room.type);
    }
    for (const id of selectedStandalones) recipeIds.push(id);
    return { name, rooms: bareRooms, recipeIds };
  }, [name, vibeKeys, selectedRooms, selectedStandalones]);

  const previewSubjects: PreviewSubject[] = useMemo(() => {
    const list: PreviewSubject[] = [];
    for (const room of AVAILABLE_ROOMS) {
      if (!selectedRooms.has(room.type)) continue;
      const matchedId = resolveRecipeForRoom(room.type, vibeKeys);
      list.push({
        kind: "preset",
        room,
        recipe: matchedId ? (RECIPES_BY_ID[matchedId] ?? null) : null,
      });
    }
    for (const id of selectedStandalones) {
      const recipe = RECIPES_BY_ID[id];
      if (recipe) list.push({ kind: "standalone", recipe });
    }
    return list;
  }, [vibeKeys, selectedRooms, selectedStandalones]);

  async function onSubmitCreate() {
    setCreating(true);
    try {
      await create(seedArgs);
      setStep("invite");
    } catch (error) {
      toast.error(said(error, t("errors.createFailed")));
    } finally {
      setCreating(false);
    }
  }

  async function onJoin(event: React.FormEvent) {
    event.preventDefault();
    setJoining(true);
    try {
      await accept({ code });
      navigate({ to: "/" });
    } catch (error) {
      toast.error(said(error, t("errors.invalidInvite")));
    } finally {
      setJoining(false);
    }
  }

  // ───── Join branch: simple form, no wizard chrome ─────
  if (mode === "join") {
    return (
      <main className="mx-auto flex min-h-svh w-full max-w-3xl flex-col gap-12 px-6 py-10 sm:py-16">
        <header className="flex items-center justify-between gap-4">
          <Wordmark />
          <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">
            {t("join.eyebrow")}
          </span>
        </header>

        <section className="grid gap-4">
          <h1 className="font-display text-3xl sm:text-display tracking-tight leading-[0.95] text-balance text-ink">
            <Trans
              t={t}
              i18nKey="join.title"
              components={{
                i: <span className="italic" />,
                accent: <span className="text-accent" />,
              }}
            />
          </h1>
          <p className="max-w-prose text-base text-ink-3 leading-relaxed">
            {t("join.helper")}
          </p>
        </section>

        <form onSubmit={onJoin} className="grid gap-6 max-w-md">
          <div className="grid gap-3">
            <Label htmlFor="invite">{t("join.codeLabel")}</Label>
            <Input
              id="invite"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD2345"
              maxLength={8}
              autoFocus
              className="font-display text-2xl tracking-[0.3em] num"
            />
          </div>
          <Button
            type="submit"
            size="lg"
            disabled={joining || code.length < 4}
            className="justify-self-start"
          >
            {joining ? "…" : t("join.submit")}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => setMode("create")}
          className="self-start text-sm text-ink-3 hover:text-ink underline underline-offset-2 decoration-line-strong hover:decoration-ink"
        >
          {t("join.backToCreate")}
        </button>

        <Toaster />
      </main>
    );
  }

  // ───── Create branch: 5-step wizard ─────

  const body = renderStep({
    step,
    name,
    setName,
    vibeKeys,
    toggleVibe,
    selectedRooms,
    toggleRoom,
    selectAllRooms,
    selectedStandalones,
    toggleStandalone,
    showStandalones,
    setShowStandalones,
    previewSubjects,
    onJoinMode: () => setMode("join"),
    onSkipInvite: () => navigate({ to: fromPlans ? "/plans" : "/" }),
    fromPlans,
    setFromPlans,
  });

  const totalRooms = selectedRooms.size + selectedStandalones.size;
  const continueDisabled =
    (step === "name" && name.trim().length === 0) ||
    (step === "rooms" && totalRooms === 0);

  const continueLabel =
    step === "preview"
      ? t("wizard.createWorkspace")
      : t("actions.continue", { ns: "common" });

  const helper = helperKey(step);

  const steps: WizardStep[] = STEPS.map((s) => ({
    key: s.key,
    label: t(s.labelKey),
  }));

  function goNext() {
    if (step === "name") setStep("vibe");
    else if (step === "vibe") setStep("rooms");
    else if (step === "rooms") setStep("preview");
    else if (step === "preview") void onSubmitCreate();
  }

  function goBack() {
    if (step === "vibe") setStep("name");
    else if (step === "rooms") setStep("vibe");
    else if (step === "preview") setStep("rooms");
    // No back from invite — workspace is already created.
  }

  return (
    <>
      <WizardShell
        steps={steps}
        activeKey={step}
        eyebrow={t(`eyebrows.${step}`)}
        title={renderTitle(t, step, name)}
        helper={helper ? t(helper) : null}
        onBack={step === "name" || step === "invite" ? undefined : goBack}
        onContinue={step === "invite" ? undefined : goNext}
        continueLabel={continueLabel}
        continueDisabled={continueDisabled}
        busy={creating}
        hideFooter={step === "invite"}
      >
        {body}
      </WizardShell>
      <Toaster />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step body rendering
// ─────────────────────────────────────────────────────────────────────────────

function renderStep(props: {
  step: Step;
  name: string;
  setName: (s: string) => void;
  vibeKeys: string[];
  toggleVibe: (k: string) => void;
  selectedRooms: Set<string>;
  toggleRoom: (t: string) => void;
  selectAllRooms: () => void;
  selectedStandalones: Set<string>;
  toggleStandalone: (id: string) => void;
  showStandalones: boolean;
  setShowStandalones: (b: boolean) => void;
  previewSubjects: PreviewSubject[];
  onJoinMode: () => void;
  onSkipInvite: () => void;
  fromPlans: boolean;
  setFromPlans: (b: boolean) => void;
}) {
  switch (props.step) {
    case "name":
      return <NameStep {...props} />;
    case "vibe":
      return <VibeStep {...props} />;
    case "rooms":
      return <RoomsStep {...props} />;
    case "preview":
      return <PreviewStep subjects={props.previewSubjects} />;
    case "invite":
      return <InviteStep onSkip={props.onSkipInvite} />;
  }
}

function NameStep({
  name,
  setName,
  onJoinMode,
}: {
  name: string;
  setName: (s: string) => void;
  onJoinMode: () => void;
}) {
  const { t } = useTranslation("onboarding");
  return (
    <>
      <div className="grid gap-3 max-w-md">
        <Label htmlFor="ws-name">{t("name.label")}</Label>
        <Input
          id="ws-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("name.defaultName")}
          maxLength={60}
          autoFocus
          className="font-display text-xl sm:text-2xl"
        />
        <p className="text-xs text-ink-3">
          {t("name.hint")}
        </p>
      </div>

      <div className="flex items-center gap-3 text-sm text-ink-3">
        <span>{t("name.joinPrompt")}</span>
        <button
          type="button"
          onClick={onJoinMode}
          className="text-ink underline underline-offset-2 decoration-line-strong hover:decoration-ink"
        >
          {t("name.useCode")}
        </button>
      </div>
    </>
  );
}

function VibeStep({
  vibeKeys,
  toggleVibe,
}: {
  vibeKeys: string[];
  toggleVibe: (k: string) => void;
}) {
  const { t } = useTranslation("onboarding");
  const atCap = vibeKeys.length >= MAX_VIBES;
  return (
    <>
      <div className="flex items-end justify-between border-b border-line-strong pb-2">
        <Label>
          <Trans
            t={t}
            i18nKey="vibe.counter"
            values={{ selected: vibeKeys.length, max: MAX_VIBES }}
            components={{
              num: <span className="num tabular-nums" />,
              muted: <span className="text-ink-4" />,
            }}
          />
        </Label>
        <span className="text-xs text-ink-3">
          {t("vibe.hint", { max: MAX_VIBES })}
        </span>
      </div>

      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {VIBES.map((vibe: Vibe) => {
          const selected = vibeKeys.includes(vibe.key);
          return (
            <li key={vibe.key}>
              <VibeCard
                vibe={vibe}
                selected={selected}
                onToggle={() => toggleVibe(vibe.key)}
                disabled={!selected && atCap}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
}

function RoomsStep({
  vibeKeys,
  selectedRooms,
  toggleRoom,
  selectAllRooms,
  selectedStandalones,
  toggleStandalone,
  showStandalones,
  setShowStandalones,
  fromPlans,
  setFromPlans,
}: {
  fromPlans: boolean;
  setFromPlans: (b: boolean) => void;
  vibeKeys: string[];
  selectedRooms: Set<string>;
  toggleRoom: (t: string) => void;
  selectAllRooms: () => void;
  selectedStandalones: Set<string>;
  toggleStandalone: (id: string) => void;
  showStandalones: boolean;
  setShowStandalones: (b: boolean) => void;
}) {
  const { t } = useTranslation("onboarding");
  const total = selectedRooms.size + selectedStandalones.size;
  return (
    <>
      <div className="flex items-end justify-between border-b border-line-strong pb-2">
        <Label>
          <Trans
            t={t}
            i18nKey="rooms.counter"
            values={{ total }}
            components={{
              num: <span className="num tabular-nums" />,
              muted: <span className="font-normal text-ink-3" />,
            }}
          />
        </Label>
        <div className="flex gap-3 text-xs">
          <button
            type="button"
            onClick={selectAllRooms}
            className="text-ink-3 hover:text-ink underline underline-offset-2 decoration-line-strong hover:decoration-ink"
          >
            {t("rooms.allPresets")}
          </button>
          <button
            type="button"
            onClick={() => {
              for (const r of AVAILABLE_ROOMS) {
                if (selectedRooms.has(r.type)) toggleRoom(r.type);
              }
              for (const id of Array.from(selectedStandalones)) toggleStandalone(id);
            }}
            className="text-ink-3 hover:text-ink underline underline-offset-2 decoration-line-strong hover:decoration-ink"
          >
            {t("rooms.none")}
          </button>
        </div>
      </div>

      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {AVAILABLE_ROOMS.map((room) => (
          <li key={room.type}>
            <RoomTile
              room={room}
              selected={selectedRooms.has(room.type)}
              onToggle={() => toggleRoom(room.type)}
              vibeKeys={vibeKeys}
            />
          </li>
        ))}
      </ul>

      <div className="grid gap-3">
        <button
          type="button"
          onClick={() => setShowStandalones(!showStandalones)}
          className="flex items-end justify-between gap-3 border-b border-line-strong pb-2 text-start"
        >
          <Label className="cursor-pointer">
            <Trans
              t={t}
              i18nKey="rooms.standaloneLabel"
              components={{
                muted: <span className="font-normal text-ink-3 italic" />,
              }}
            />
          </Label>
          <span className="shrink-0 whitespace-nowrap text-xs text-ink-3 hover:text-ink underline underline-offset-2 decoration-line-strong">
            {showStandalones
              ? t("rooms.hide")
              : t("rooms.show", { n: STANDALONE_RECIPES.length })}
          </span>
        </button>
        {showStandalones ? (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {STANDALONE_RECIPES.map((recipe) => (
              <li key={recipe.id}>
                <StandaloneRecipeTile
                  recipe={recipe}
                  selected={selectedStandalones.has(recipe.id)}
                  onToggle={() => toggleStandalone(recipe.id)}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-md border border-line px-4 py-3">
        <Checkbox checked={fromPlans} onCheckedChange={(v) => setFromPlans(v === true)} className="mt-0.5" />
        <span className="grid gap-0.5">
          <span className="text-sm text-ink">{t("onboarding.title", { ns: "plans" })}</span>
          <span className="text-xs text-ink-3">{t("onboarding.body", { ns: "plans" })}</span>
        </span>
      </label>
    </>
  );
}

function PreviewStep({ subjects }: { subjects: PreviewSubject[] }) {
  const { t } = useTranslation("onboarding");
  if (subjects.length === 0) {
    return (
      <p className="font-display italic text-lg text-ink-3">
        {t("preview.empty")}
      </p>
    );
  }
  return (
    <>
      <ul className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
        {subjects.map((s, idx) => (
          <li key={subjectKey(s, idx)}>
            <PreviewRoomCard subject={s} />
          </li>
        ))}
      </ul>
      <aside
        className={cn(
          "rounded-lg border border-line bg-paper-2/40 p-4 sm:p-5 text-sm leading-relaxed text-ink-2",
        )}
      >
        <p className="font-display italic text-lg text-ink leading-snug">
          {t("preview.nextTitle")}
        </p>
        <p className="mt-1 text-ink-3 max-w-prose">
          {t("preview.nextBody")}
        </p>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step copy
// ─────────────────────────────────────────────────────────────────────────────

const TITLE_COMPONENTS = {
  i: <span className="italic" />,
  accent: <span className="text-accent" />,
};

function renderTitle(
  t: TFunction<"onboarding">,
  step: Step,
  name: string,
): React.ReactNode {
  const place = name.trim() || t("yourPlace");
  return (
    <Trans
      t={t}
      i18nKey={`titles.${step}`}
      values={{ place }}
      components={TITLE_COMPONENTS}
    />
  );
}

function helperKey(step: Step) {
  switch (step) {
    case "name":
    case "vibe":
    case "preview":
      return `helpers.${step}` as const;
    case "rooms":
    case "invite":
      return null;
  }
}

function subjectKey(s: PreviewSubject, idx: number): string {
  if (s.kind === "preset") return `preset-${s.room.type}-${idx}`;
  return `standalone-${s.recipe.id}-${idx}`;
}
